"use client";

import dynamic from "next/dynamic";
import { useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useStudioFileAssets } from "@/app/studio/pages/[slug]/StudioFileAssetsContext";
import { STUDIO_UPLOAD_ALLOWED_EXTENSIONS } from "@/utils/studioBlobPath";

const StudioInPageAnchorSelect = dynamic(
  () =>
    import("@/app/studio/pages/[slug]/StudioInPageAnchorSelect").then(
      (module) => module.StudioInPageAnchorSelect,
    ),
  {
    ssr: false,
    loading: () => (
      <p className="font-monument text-xs text-black/60">Loading sections...</p>
    ),
  },
);

const ACCEPT = [...STUDIO_UPLOAD_ALLOWED_EXTENSIONS]
  .map((ext) => `.${ext}`)
  .join(",");

type LinkMode = "url" | "anchor";

type StudioBlobLinkFieldProps = {
  value: string;
  onChange: (value: string) => void;
};

function deriveLinkMode(value: string): LinkMode {
  return value.startsWith("#") ? "anchor" : "url";
}

export function StudioBlobLinkField({
  value,
  onChange,
}: Readonly<StudioBlobLinkFieldProps>) {
  const { assets, uploadFile } = useStudioFileAssets();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const linkMode = deriveLinkMode(value);

  const selectedAsset = assets.find((asset) => asset.url === value);

  function handleModeChange(nextMode: LinkMode) {
    if (nextMode === linkMode) {
      return;
    }

    if (nextMode === "anchor") {
      if (!value.startsWith("#")) {
        onChange("#");
      }
      return;
    }

    if (value.startsWith("#")) {
      onChange("");
    }
  }

  async function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setUploading(true);
    setError(null);
    try {
      const asset = await uploadFile(file);
      onChange(asset.url);
    } catch (uploadError) {
      setError(
        uploadError instanceof Error ? uploadError.message : "upload_failed",
      );
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        <button
          type="button"
          aria-pressed={linkMode === "url"}
          onClick={() => handleModeChange("url")}
          className={`rounded-md border px-3 py-1.5 font-monument text-xs ${
            linkMode === "url"
              ? "border-black bg-black text-white"
              : "border-black/15 text-black"
          }`}
        >
          URL / file
        </button>
        <button
          type="button"
          aria-pressed={linkMode === "anchor"}
          onClick={() => handleModeChange("anchor")}
          className={`rounded-md border px-3 py-1.5 font-monument text-xs ${
            linkMode === "anchor"
              ? "border-black bg-black text-white"
              : "border-black/15 text-black"
          }`}
        >
          In-page section
        </button>
      </div>

      {linkMode === "url" ? (
        <>
          <Input
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder="/contact-sales or https://..."
            className="font-mono text-xs"
          />
          {assets.length > 0 ? (
            <Select
              value={selectedAsset?.url ?? ""}
              onValueChange={(nextValue) => onChange(nextValue)}
            >
              <SelectTrigger className="font-monument text-xs">
                <SelectValue placeholder="Choose a page file" />
              </SelectTrigger>
              <SelectContent>
                {assets.map((asset) => (
                  <SelectItem key={asset.id} value={asset.url}>
                    {asset.filename}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={(event) => void handleFileChange(event)}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="rounded-md border border-black/15 px-3 py-1.5 font-monument text-xs disabled:opacity-50"
            >
              {uploading ? "Uploading..." : "Upload file"}
            </button>
          </div>
        </>
      ) : (
        <StudioInPageAnchorSelect value={value} onChange={onChange} />
      )}

      {error ? (
        <p className="font-monument text-xs text-red-600">{error}</p>
      ) : null}
    </div>
  );
}
